/**
 * Bilan anti-gaspi — ce que la remise par lot a rapporté et coûté.
 *
 * Demande de Philippe (27/09/2026). Pour les lots remisés, sur la période :
 * l'argent encaissé (caisse + en ligne), la remise consentie, le coût d'achat
 * des articles ainsi vendus (la perte évitée, en admettant qu'ils seraient
 * partis à la poubelle — c'est une borne haute), la marge qui reste, et ce que
 * ces lots ont encore perdu malgré la remise.
 *
 * Ne couvre que la remise PAR LOT (ses ventes portent le lot et la remise) :
 * l'ancienne promo de fiche ne laissait pas de trace ligne par ligne.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { formatPrice, formatQty } from '../../utils/pos.utils';

interface Ligne {
  lot_id: string;
  product_name: string;
  lot_number: string;
  expiry_date: string | null;
  promo_percent_actuel: number | null;
  unites_vendues: number;
  ca_ttc_cents: number;
  ca_ht_cents: number;
  remise_ttc_cents: number;
  cout_evite_ht_cents: number | null;
  marge_ht_cents: number | null;
  pertes_unites: number;
  pertes_cout_ht_cents: number;
}

interface Bilan {
  jours: number;
  lignes: Ligne[];
  totaux: {
    lots: number;
    unites_vendues: number;
    ca_ttc_cents: number;
    ca_ht_cents: number;
    remise_ttc_cents: number;
    cout_evite_ht_cents: number;
    marge_ht_cents: number;
    lots_cout_inconnu: number;
    pertes_cout_ht_cents: number;
  };
}

function dateFr(iso: string | null): string {
  if (!iso) return '—';
  const [a, m, j] = String(iso).slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

function Chiffre({ titre, valeur, aide, ton = 'neutre' }: { titre: string; valeur: string; aide: string; ton?: 'bon' | 'cout' | 'neutre' }) {
  const couleur = ton === 'bon' ? 'text-emerald-700' : ton === 'cout' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500">{titre}</div>
      <div className={`text-xl font-bold tabular-nums ${couleur}`}>{valeur}</div>
      <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{aide}</div>
    </div>
  );
}

export default function BilanAntiGaspi() {
  const [jours, setJours] = useState(30);
  const [bilan, setBilan] = useState<Bilan | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      setBilan(await api.get(`/lots/promo-bilan?days=${jours}`));
    } catch {
      setErreur('Bilan indisponible.');
    } finally {
      setLoading(false);
    }
  }, [jours]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const t = bilan?.totaux;

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-800">Bilan anti-gaspi</h2>
          <p className="text-xs text-gray-500 mt-0.5">Remises par lot, caisse et boutique en ligne.</p>
        </div>
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Sur
          <select
            value={jours}
            onChange={(e) => setJours(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {[7, 30, 90, 365].map((j) => (
              <option key={j} value={j}>
                {j} jours
              </option>
            ))}
          </select>
        </label>
      </div>

      {erreur && <p className="px-4 pt-3 text-sm font-medium text-red-700">{erreur}</p>}
      {loading && !bilan ? (
        <p className="px-4 py-6 text-sm text-gray-500">Calcul…</p>
      ) : t && t.unites_vendues === 0 && bilan!.lignes.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          Aucune remise par lot sur les {jours} derniers jours.
        </p>
      ) : t ? (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Chiffre
              titre="Argent récupéré"
              valeur={formatPrice(t.ca_ttc_cents)}
              aide={`TTC · ${formatPrice(t.ca_ht_cents)} HT, pour ${formatQty(t.unites_vendues)} unités`}
              ton="bon"
            />
            <Chiffre
              titre="Perte évitée"
              valeur={formatPrice(t.cout_evite_ht_cents)}
              aide="Coût d'achat HT des articles vendus en anti-gaspi (s'ils étaient partis à la poubelle)"
              ton="bon"
            />
            <Chiffre
              titre="Marge restante"
              valeur={formatPrice(t.marge_ht_cents)}
              aide="Argent récupéré HT − coût d'achat"
              ton={t.marge_ht_cents >= 0 ? 'bon' : 'cout'}
            />
            <Chiffre
              titre="Remise consentie"
              valeur={formatPrice(t.remise_ttc_cents)}
              aide="Ce que les clients auraient payé en plus au prix normal (TTC)"
              ton="cout"
            />
            <Chiffre
              titre="Perdu malgré tout"
              valeur={formatPrice(t.pertes_cout_ht_cents)}
              aide="Pertes encore sorties sur ces lots (coût HT)"
              ton={t.pertes_cout_ht_cents > 0 ? 'cout' : 'neutre'}
            />
          </div>
          {t.lots_cout_inconnu > 0 && (
            <p className="text-xs text-amber-700">
              {t.lots_cout_inconnu} lot{t.lots_cout_inconnu > 1 ? 's' : ''} sans prix d'achat : exclu
              {t.lots_cout_inconnu > 1 ? 's' : ''} de la perte évitée et de la marge (jamais compté pour zéro).
            </p>
          )}

          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-2">Produit · lot</th>
                  <th className="py-2 pr-2 text-right">Vendu</th>
                  <th className="py-2 pr-2 text-right">Récupéré TTC</th>
                  <th className="py-2 pr-2 text-right">Remise</th>
                  <th className="py-2 pr-2 text-right">Perte évitée</th>
                  <th className="py-2 pr-2 text-right">Marge HT</th>
                  <th className="py-2 text-right">Perdu</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {bilan!.lignes.map((l) => (
                  <tr key={l.lot_id} className="border-b border-gray-50">
                    <td className="py-2 pr-2">
                      <div className="font-medium text-gray-900">{l.product_name}</div>
                      <div className="text-xs text-gray-500">
                        lot {l.lot_number} · date {dateFr(l.expiry_date)}
                        {l.promo_percent_actuel ? ` · −${l.promo_percent_actuel} % en cours` : ''}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-right">{formatQty(l.unites_vendues)}</td>
                    <td className="py-2 pr-2 text-right">{formatPrice(l.ca_ttc_cents)}</td>
                    <td className="py-2 pr-2 text-right text-red-700">{formatPrice(l.remise_ttc_cents)}</td>
                    <td className="py-2 pr-2 text-right">
                      {l.cout_evite_ht_cents == null ? <span className="text-amber-700">coût ?</span> : formatPrice(l.cout_evite_ht_cents)}
                    </td>
                    <td className="py-2 pr-2 text-right">{l.marge_ht_cents == null ? '—' : formatPrice(l.marge_ht_cents)}</td>
                    <td className="py-2 text-right">
                      {l.pertes_cout_ht_cents > 0 ? formatPrice(l.pertes_cout_ht_cents) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500">
            « Perte évitée » compte chaque article vendu en anti-gaspi comme sauvé de la poubelle : c'est une
            borne haute, une partie se serait peut-être vendue au prix normal.
          </p>
        </div>
      ) : null}
    </div>
  );
}
